import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminAPI } from "../../services/api/admin";

const FEATURE_KEYS = ["observability", "llm_metrics", "analytics", "api_access", "schedules",
                       "alerts", "prompt_optimizer", "discovery", "sso", "sla"];
// Display labels for feature keys whose humanized key isn't the product name.
// The `discovery` key powers the Co-pilot feature (key kept for compatibility).
const FEATURE_LABELS = { discovery: "Co-pilot" };
const featureLabel = (k) => FEATURE_LABELS[k] || k.replace(/_/g, " ");

const DEFAULT_FORM = {
  code: "",
  name: "",
  monthly_price_cents: "",
  interval: "month",
  monthly_credits: 0,
  max_users: "",
  max_agents: "",
  max_concurrent_runs: "",
  max_agent_metrics: "",
  max_global_metrics: "",
  max_copilot_daily_turns: "",
  is_public: true,
  billing_mode: "card",
  credit_grant_trigger: "invoice_paid",
  sort_order: 10,
  active: true,
  scoped_to_billing_account_id: "",
  features: Object.fromEntries(FEATURE_KEYS.map((k) => [k, false])),
};

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const showMsg = (type, text) => {
    setMsg({ type, text });
    if (text) setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const load = async () => {
    setLoading(true);
    try {
      setPlans(await adminAPI.getPlans());
    } catch (e) {
      showMsg("error", e.response?.data?.error || "Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...DEFAULT_FORM, features: Object.fromEntries(FEATURE_KEYS.map((k) => [k, false])) });
    setModalOpen(true);
  };

  const openEdit = (plan) => {
    setEditing(plan);
    setForm({
      ...DEFAULT_FORM,
      ...plan,
      monthly_price_cents: plan.monthly_price_cents ?? "",
      max_users: plan.max_users ?? "",
      max_agents: plan.max_agents ?? "",
      max_concurrent_runs: plan.max_concurrent_runs ?? "",
      max_agent_metrics: plan.max_agent_metrics ?? "",
      max_global_metrics: plan.max_global_metrics ?? "",
      max_copilot_daily_turns: plan.max_copilot_daily_turns ?? "",
      scoped_to_billing_account_id: plan.scoped_to_billing_account_id ?? "",
      features: { ...Object.fromEntries(FEATURE_KEYS.map((k) => [k, false])), ...(plan.features || {}) },
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const toNumOrNull = (v) => (v === "" || v == null ? null : Number(v));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const values = {
        ...form,
        monthly_price_cents: toNumOrNull(form.monthly_price_cents),
        monthly_credits: toNumOrNull(form.monthly_credits) ?? 0,
        max_users: toNumOrNull(form.max_users),
        max_agents: toNumOrNull(form.max_agents),
        max_concurrent_runs: toNumOrNull(form.max_concurrent_runs),
        max_agent_metrics: toNumOrNull(form.max_agent_metrics),
        max_global_metrics: toNumOrNull(form.max_global_metrics),
        max_copilot_daily_turns: toNumOrNull(form.max_copilot_daily_turns),
        sort_order: toNumOrNull(form.sort_order) ?? 0,
        scoped_to_billing_account_id: toNumOrNull(form.scoped_to_billing_account_id),
      };
      if (editing) {
        await adminAPI.updatePlan(editing.id, values);
        showMsg("success", "Plan updated");
      } else {
        await adminAPI.createPlan(values);
        showMsg("success", "Plan created");
      }
      closeModal();
      load();
    } catch (err) {
      const errBody = err.response?.data?.error;
      const text = Array.isArray(errBody) ? errBody.join(", ") : (errBody || "Save failed");
      showMsg("error", text);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmArchive = async (plan) => {
    if (!window.confirm(`Archive plan "${plan.code}"?`)) return;
    try {
      await adminAPI.archivePlan(plan.id);
      showMsg("success", "Plan archived");
      load();
    } catch (e) {
      showMsg("error", e.response?.data?.error || "Archive failed");
    }
  };

  const sync = async (id) => {
    try {
      await adminAPI.syncPlanStripe(id);
      showMsg("success", "Synced with Stripe");
      load();
    } catch (e) {
      showMsg("error", e.response?.data?.error || "Sync failed");
    }
  };

  const fmtPrice = (cents) => (cents != null ? `$${(cents / 100).toFixed(0)}/mo` : "—");

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-foreground">Plans</h1>
        <button
          onClick={openNew}
          className="px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800"
        >
          + New Plan
        </button>
      </div>

      {msg.text && (
        <div className={`mb-4 rounded-lg px-4 py-2.5 text-sm ${msg.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-600 border border-red-100"}`}>
          {msg.text}
        </div>
      )}

      <div className="bg-card rounded-lg border border-border overflow-hidden mb-6">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Code</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Price</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Credits</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Billing</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Stripe</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Subs</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Public</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Active</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={10} className="px-6 py-8 text-center text-muted-foreground">Loading...</td></tr>
            ) : plans.length === 0 ? (
              <tr><td colSpan={10} className="px-6 py-8 text-center text-muted-foreground">No plans yet</td></tr>
            ) : plans.map((plan) => (
              <tr key={plan.id} className="hover:bg-muted/50">
                <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground font-medium">{plan.code}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">{plan.name}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">{fmtPrice(plan.monthly_price_cents)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">{plan.monthly_credits ?? 0}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                  <div>{plan.billing_mode}</div>
                  <div className="text-xs text-muted-foreground">{plan.credit_grant_trigger}</div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`px-2 py-0.5 inline-flex text-xs font-medium rounded-full ${plan.stripe_price_id ? "bg-green-50 text-green-700" : "bg-muted text-foreground"}`}>
                    {plan.stripe_price_id ? "synced" : "unsynced"}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">{plan.subscription_count ?? 0}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`px-2 py-0.5 inline-flex text-xs font-medium rounded-full ${plan.is_public ? "bg-green-50 text-green-700" : "bg-purple-50 text-purple-700"}`}>
                    {plan.is_public ? "public" : "private"}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`px-2 py-0.5 inline-flex text-xs font-medium rounded-full ${plan.active ? "bg-green-50 text-green-700" : "bg-muted text-foreground"}`}>
                    {plan.active ? "active" : "archived"}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  <div className="flex items-center gap-3 justify-end">
                    <button onClick={() => openEdit(plan)} className="text-sm text-foreground hover:underline">Edit</button>
                    <Link to={`/admin/plans/${plan.id}/feature-assignments`} className="text-sm text-foreground hover:underline">Manage features</Link>
                    <button onClick={() => sync(plan.id)} className="text-sm text-foreground hover:underline">Sync Stripe</button>
                    {plan.active && (
                      <button onClick={() => confirmArchive(plan)} className="text-sm text-red-600 hover:underline">Archive</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={closeModal}>
          <div className="bg-card rounded-lg w-full max-w-2xl shadow-xl p-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground">
                {editing ? `Edit Plan: ${editing.code}` : "New Plan"}
              </h3>
              <button onClick={closeModal} className="text-muted-foreground hover:text-muted-foreground text-2xl leading-none">&times;</button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Code</label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="enterprise-acme"
                    required
                    disabled={!!editing}
                    className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-black focus:border-black disabled:bg-muted/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Display name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Enterprise — Acme"
                    required
                    className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-black focus:border-black"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Monthly price (cents)</label>
                  <input
                    type="number"
                    min="0"
                    value={form.monthly_price_cents}
                    onChange={(e) => setForm({ ...form, monthly_price_cents: e.target.value })}
                    placeholder="e.g. 30000 for $300"
                    className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-black focus:border-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Interval</label>
                  <select
                    value={form.interval}
                    onChange={(e) => setForm({ ...form, interval: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-black focus:border-black"
                  >
                    <option value="month">Monthly</option>
                    <option value="year">Yearly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Monthly credits</label>
                  <input
                    type="number"
                    min="0"
                    value={form.monthly_credits}
                    onChange={(e) => setForm({ ...form, monthly_credits: e.target.value })}
                    required
                    className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-black focus:border-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Sort order</label>
                  <input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-black focus:border-black"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Max users (blank = unlimited)</label>
                  <input
                    type="number"
                    min="1"
                    value={form.max_users}
                    onChange={(e) => setForm({ ...form, max_users: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-black focus:border-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Max agents (blank = unlimited)</label>
                  <input
                    type="number"
                    min="1"
                    value={form.max_agents}
                    onChange={(e) => setForm({ ...form, max_agents: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-black focus:border-black"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Max concurrent runs</label>
                  <input
                    type="number"
                    min="1"
                    value={form.max_concurrent_runs}
                    onChange={(e) => setForm({ ...form, max_concurrent_runs: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-black focus:border-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Max metrics per agent (blank = unlimited)</label>
                  <input
                    type="number"
                    min="1"
                    value={form.max_agent_metrics}
                    onChange={(e) => setForm({ ...form, max_agent_metrics: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-black focus:border-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Max global metrics (blank = unlimited)</label>
                  <input
                    type="number"
                    min="1"
                    value={form.max_global_metrics}
                    onChange={(e) => setForm({ ...form, max_global_metrics: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-black focus:border-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Max copilot turns / day (blank = unlimited)</label>
                  <input
                    type="number"
                    min="1"
                    value={form.max_copilot_daily_turns}
                    onChange={(e) => setForm({ ...form, max_copilot_daily_turns: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-black focus:border-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Scoped to billing account (private only)</label>
                  <input
                    type="number"
                    value={form.scoped_to_billing_account_id}
                    onChange={(e) => setForm({ ...form, scoped_to_billing_account_id: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-black focus:border-black"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Billing mode</label>
                  <select
                    value={form.billing_mode}
                    onChange={(e) => setForm({ ...form, billing_mode: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-black focus:border-black"
                  >
                    <option value="card">Card (auto-charge)</option>
                    <option value="invoice">Invoice (NET 30)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Credit grant trigger</label>
                  <select
                    value={form.credit_grant_trigger}
                    onChange={(e) => setForm({ ...form, credit_grant_trigger: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-black focus:border-black"
                  >
                    <option value="invoice_paid">On invoice paid</option>
                    <option value="invoice_issued">On invoice issued</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={!!form.is_public}
                    onChange={(e) => setForm({ ...form, is_public: e.target.checked })}
                    className="h-4 w-4 rounded border-input"
                  />
                  Public (shown on /pricing)
                </label>
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={!!form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    className="h-4 w-4 rounded border-input"
                  />
                  Active
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Features</label>
                <div className="grid grid-cols-2 gap-2 p-3 border border-border rounded-lg bg-muted/50">
                  {FEATURE_KEYS.map((k) => (
                    <label key={k} className="flex items-center gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        checked={!!form.features?.[k]}
                        onChange={(e) => setForm({ ...form, features: { ...form.features, [k]: e.target.checked } })}
                        className="h-4 w-4 rounded border-input"
                      />
                      {featureLabel(k)}
                    </label>
                  ))}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Note: this is the legacy plan-level feature flag set. Per-sub-feature mapping is now managed via "Manage features" on each plan row.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-border mt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm text-foreground border border-input rounded-lg hover:bg-muted/50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  {submitting ? "Saving..." : editing ? "Save" : "Create & sync"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
